import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom';
import { useContext } from 'react';
import { WorkspaceContext } from '../../WorkspaceContext';
import { useParams } from 'react-router-dom';
import type { RoomMemberDetails } from '../SideBarTypes';
import api from '../../../../api';
import Star from '../../../../shared/components/svgIcons/Star';
import EditIcon from '../../../../shared/components/svgIcons/EditIcon';
import UserIcon from '../../../../shared/components/svgIcons/UserIcon';
import '../sidebar.scss'
import styles from './MemberTab.module.scss'
import { socket } from '../../../../Socket';
import type { Member } from '../../WorkspaceContext';

const MemberTab = () => {

    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error("Workspace must be wrapped in WorkspaceContext.Provider");
    const { ctheme, roomDetails, membersList, memberDetails, activeMembers, setLimitMessage, setLimitPopup } = ctx;

    const [memberSettingPopup, setMemberSettingPopup] = useState(false);
    const [popupMemberId, setPopupMemberId] = useState('')
    const [popupMemberData, setPopupMemberData] = useState<RoomMemberDetails | null>(null)
    const [memberRoleVal, setMemberRoleVal] = useState('');
    const [memberEditorVal, setMemberEditorVal] = useState('')
    const [memberSettingsPortal, setMemberSettingsPortal] = useState<HTMLElement | null>(null);

    const { roomId } = useParams();
    const clientId = localStorage.getItem("flashroom_id");


    const setInitialRole = () => {
        const role = memberDetails?.role;
        if (!role) return 'member';

        return role;
    }
    const [roleRef, setRoleRef] = useState<'owner' | 'admin' | 'member'>(setInitialRole());


    const [memberMode, setMemberMode] = useState('active');

    useEffect(() => {
        setMemberSettingsPortal(document.getElementById('sidebar-inner'));
    }, []);

    const changeMemberList = (mode: string) => {
        setMemberMode(mode);
    }

    const memberRenderList = memberMode == 'active' ? membersList.filter((m) => m.isActive) : membersList



    const editMemberSettingsClick = (id: string, details: RoomMemberDetails | null) => {
        if (!details) return; // safety

        setMemberSettingPopup(true);
        setPopupMemberId(id);
        setPopupMemberData(details);

        setMemberEditorVal(details.canCreate ? 'true' : 'false');
        setMemberRoleVal(details.role ?? '');
    };

    const closeMemberSettingClick = () => {
        setMemberSettingPopup(false);
        setPopupMemberId('');
        setPopupMemberData(null);
    }

    const kickMemberClick = async (clientId: string) => {
        try {
            const res = await api.patch(`/rooms/${roomId}/kick`,
                { memberClientId: clientId }
            )

            setMemberSettingPopup(false)

            if (!res.data.ok) {
                setLimitMessage(`Failed to kick member: ${res.data.error}`);
                setLimitPopup(true);
                return;
            }

            // success: maybe remove member from UI or wait for 'members:refresh'
            // setMembers(prev => prev.filter(m => m.clientId !== clientId));
        } catch (error) {
            setLimitMessage("Failed to kick member. Try again.");
            setLimitPopup(true);
        }
    };





    const chooseMemberIcon = (member: RoomMemberDetails) => {

        const viewerRole = memberDetails?.role;
        const viewerClientId = memberDetails?.clientId;
        const creatorClientId = roomDetails?.creatorClientId;


        if (member.role === 'owner') {
            return <Star />
        }

        if (member.role === 'admin') {
            return <EditIcon />
        }

        if (member.role === 'member') {
            return <UserIcon />
        }

    }



    const showMemberSettingsButton = (member: RoomMemberDetails) => {
        const viewerRole = memberDetails?.role;          // "owner" | "admin" | "member"
        const viewerClientId = memberDetails?.clientId;
        const creatorClientId = roomDetails?.creatorClientId;

        // 1) Only owners or admins can see any settings at all
        if (viewerRole !== "owner" && viewerRole !== "admin") return false;

        // 2) Never allow editing the creator/owner row (from anyone, including themselves)
        if (member.role === "owner") return false;
        if (creatorClientId && member.clientId === creatorClientId) return false;

        // 3) Never allow editing your own row (owner OR admin)
        if (viewerClientId && member.clientId === viewerClientId) return false;

        // 4) Admins can't edit other admins
        if (viewerRole === "admin" && member.role === "admin") return false;


        return true;
    };




    const changeUserPermClick = async (memberId: string, role: string, editor: string) => {
        try {
            if (editor !== 'true' && editor !== 'false') {
                return;
            }

            await api.patch(`/rooms/${roomId}/members/update/${memberId}`,
                {
                    role,
                    canCreate: editor
                })
            closeMemberSettingClick();

        } catch (error) {
            setLimitMessage('Could not update member permissions. Please try again.');
            setLimitPopup(true);
        }
    }

    const memberSettingsPopup = popupMemberData && memberSettingPopup ? (
        <div className='member-settings-popup' id='member-settings-popup'>
            <div className={`${ctheme === 'dark' ? styles.mspDark : styles.mspLight}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className='quick-flex'>
                        <div className={styles.memberIcon} style={{ backgroundColor: popupMemberData.iconColor }}>
                            <span className={styles.letter}>{popupMemberData.name.slice(0, 1)}</span>
                        </div>
                        <h5>{popupMemberData.name}</h5>
                    </div>
                    <div id='mem-pop-cancel' onClick={closeMemberSettingClick}>
                        <svg className="x-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01">
                            <g id="Layer_3" data-name="Layer 3">
                                <path
                                    className="cls-1"
                                    d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                    transform="translate(-124.17 -181.62)"
                                />
                            </g>
                        </svg>
                    </div>
                </div>



                <div className={styles.mpSettingsRow}>
                    <span className={styles.lightText}>Role</span>

                    <div className={`quick-flex ${styles.radContainer}`}>
                        <span >Member</span>
                        <input
                            className={styles.mpRad}
                            type='radio'
                            name={`role-${popupMemberId}`}
                            value='member'
                            checked={memberRoleVal === 'member'}
                            onChange={() =>
                                setMemberRoleVal('member')
                            }
                        />
                    </div>

                    <div className={`quick-flex ${styles.radContainer}`}>
                        <span>Admin</span>
                        <input
                            className={styles.mpRad}
                            type='radio'
                            name={`role-${popupMemberId}`}
                            value='admin'
                            checked={memberRoleVal === 'admin'}
                            onChange={() => {
                                setMemberRoleVal('admin');
                                setMemberEditorVal('true');
                            }}
                        />
                    </div>


                </div>



                <div className={styles.mpSettingsRow}>
                    <span className={styles.lightText}>Permissions</span>
                    <div className={`quick-flex ${styles.radContainer}`}>
                        <span>Editor</span>
                        <input
                            className={styles.mpRad}
                            type='radio'
                            name={`create-${popupMemberId}`}
                            value='allowed'
                            checked={memberEditorVal === 'true'}
                            onChange={() =>
                                setMemberEditorVal('true')
                            }
                        />
                    </div>

                    <div className={`quick-flex ${styles.radContainer}`}>
                        <span>Viewer</span>
                        <input
                            className={styles.mpRad}
                            type='radio'
                            name={`create-${popupMemberId}`}
                            value='disabled'
                            checked={memberEditorVal === 'false'}
                            onChange={() =>
                                setMemberEditorVal('false')
                            }
                        />
                    </div>

                </div>



                <div className={styles.memberPopupBtns}>
                    {roleRef == 'owner' && (
                        <button onClick={() => kickMemberClick(popupMemberData.clientId)} className={styles.kickButton}>Kick</button>
                    )}

                    <button onClick={() => changeUserPermClick(popupMemberData.clientId, memberRoleVal, memberEditorVal)} className={styles.savebuttton}>Save Changes</button>
                </div>
            </div>
        </div>
    ) : null;

    return (
        <div className='sb-tab-flx-cont'>
            {memberSettingsPortal && memberSettingsPopup ? createPortal(memberSettingsPopup, memberSettingsPortal) : null}




            <div id='mode-people'>
                <div id='mode-p-outline' className='sb-mode-c'>
                    <div id='mode-p-top'>
                        <h3>Members</h3>

                        <div className={styles.sbtb1}>
                            <div className={styles.sbTF}>
                                <span onClick={() => setMemberMode('active')} className={`clickable ${memberMode === 'active' ? styles.mmAt : ''}`}>Active</span>

                                <span onClick={() => setMemberMode('all')} className={`clickable ${memberMode === 'all' ? styles.mmAt : ''}`}>All</span>

                                {/* <span onClick={() => setMemberMode('banned')} className={`clickable ${memberMode === 'banned' ? 'mm-at' : ''}`}>Banned</span> */}
                            </div>
                        </div>

                    </div>



                    <div className={styles.memberTabs}>

                        {
                            memberRenderList.map((member) => (
                                <div className={`${styles.memberTab} ${member.clientId === clientId ? styles.yourMemberTab : ''}`}
                                    key={member.clientId}>
                                    <div className={styles.memberIcon} style={{ backgroundColor: `${member.iconColor}` }}>
                                        <span>{member.name.slice(0, 1).toUpperCase()}</span>
                                    </div>
                                    <span className={styles.mName}>{member.name}</span>


                                    {showMemberSettingsButton(member) ? (
                                        <div className={styles.userSettingsBtn} onClick={() => editMemberSettingsClick(member.clientId, member)}>
                                            {
                                                ctheme === 'dark' ? (
                                                    <img draggable={false} src={`${process.env.PUBLIC_URL}/images/user-settings-icon-dark.png`} width='20px' />

                                                ) : (
                                                    <img draggable={false} src={`${process.env.PUBLIC_URL}/images/user-settings-icon-light.png`} width='20px' />

                                                )
                                            }

                                        </div>
                                    ) : (
                                        <div>
                                            {chooseMemberIcon(member)}
                                        </div>
                                    )}

                                </div>
                            ))
                        }
                    </div>

                </div>

            </div>

        </div>
    )
}

export default MemberTab
